<template>
  <HeadTitle
    title="Grupos de periodos de Calendario"
    description="Lista de periodos de calendario de un grupo de nóminas"
  />
  <Toolset
    ref="toolset"
    :searcher="searcher"
    add-button-icon="pi pi-plus"
    add-button-label="Crear grupo de periodos de calendario"
    :to-add="toAdd"
    class="mt-5"
  />
  <br />
  <DataTable
    class="tabla mb-5"
    :value="calendarPeriodsGroups.data"
    paginator
    :rows="pagination.limit"
    :rowsPerPageOptions="[5, 10, 20, 50]"
    tableStyle="min-width: 50rem"
    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
    :totalRecords="calendarPeriodsGroups.totalItems"
    :currentPageReportTemplate="currentPageReportTemplate"
    scrollable
    scrollHeight="600px"
  >
    <template #empty>
      No se encontraron grupos de periodos de calendario.
    </template>
    <Column field="description" header="Descripción">
      <template #body="{ data, field }">
        {{ data[field] }}
      </template>
    </Column>
    <Column field="frequency" sortable header="Frecuencia">
      <template #body="{ data, field }">
        {{ tranlateFrequency(data[field]) }}
      </template>
    </Column>
    <Column field="onlyPayrolls" sortable header="Cant. Nóminas">
      <template #body="{ data, field }">
        <span v-if="data[field].length === 0">Todas las del grupo</span>
        <span v-else>{{ data[field].length }}</span>
      </template>
    </Column>
    <Column field="periodsCount" sortable header="Cant. Periodos"></Column>
    <Column field="fromYear" sortable header="Año desde">
      <template #body="{ data, field }">
        {{ data[field] }}
      </template>
    </Column>
    <Column field="toYear" sortable header="Año hasta">
      <template #body="{ data, field }">
        {{ data[field] }}
      </template>
    </Column>
    <Column field="updatedAt" sortable header="Ultima actualización">
      <template #body="{ data, field }">
        <span v-local-date="data[field]"></span>
      </template>
    </Column>
    <Column header="Acciones">
      <template #body="{ data }">
        <ConfirmPopup></ConfirmPopup>
        <div class="actions">
          <Button
            v-tooltip.bottom="'Ver detalles'"
            class="action-btn view p-button-sm mr-2"
            icon="pi pi-eye"
            @click="goToDetails(data['id'])"
          />
          <Button
            class="action-btn view p-button-sm"
            icon="pi pi-trash"
            @click="onDeleteCalendarGroup($event, data['id'])"
            severity="danger"
            v-tooltip.bottom="'Eliminar'"
          />
        </div>
      </template>
    </Column>
  </DataTable>
  <Footer>
    <template #left>
      <AuditButton
        :entities="['CalendarPeriodsGroup']"
        label="Ver registros eliminados"
        :root-id="payrollConfigId"
        only-deleted
      />
    </template>
    <Button
      label="Volver"
      severity="secondary"
      @click="router.push('/payrollsConfig/')"
      class="p-button-text text-sm font-light text-primary"
      icon="pi pi-angle-left"
    />
  </Footer>
</template>
<script setup lang="ts">
  import HeadTitle from '@/shared/components/HeadTitle.vue'
  import Toolset from '@/shared/components/Toolset.vue'
  import Footer from '@/shared/components/Footer.vue'
  import AuditButton from '@/shared/components/AuditButton.vue'

  import useLoading from '@/store/load'
  import { computed, onMounted, reactive, ref } from 'vue'
  import { useRoute } from 'vue-router'
  import {
    ListCalendarPeriodsGroups,
    deleteCalendarPeriodsGroup,
  } from '../services/CalendarPeriodsService'
  import { CalendarPeriodsGroupListDto } from '../models/CalendarPeriodsGroupListDto'

  import router from '@/router'
  import { useConfirm } from 'primevue/useconfirm'
  import useNotifications from '@/store/notification'

  const path = useRoute()
  const loadingStore = useLoading()
  const confirm = useConfirm()
  const notificationStore = useNotifications()
  const payrollConfigId = ref()
  const toolset = ref()

  const calendarPeriodsGroups = reactive({
    data: [] as CalendarPeriodsGroupListDto[],
    totalItems: 0,
  })

  onMounted(async () => {
    const { id } = path.params
    payrollConfigId.value = id
    await fetchData()
  })

  const fetchData = async () => {
    loadingStore.setLoadingState()
    const { id } = path.params
    payrollConfigId.value = id
    const response = await ListCalendarPeriodsGroups(
      payrollConfigId.value,
      toolset.value?.search ?? ''
    )
    calendarPeriodsGroups.data = response.data
    calendarPeriodsGroups.totalItems = response.totalItems
    loadingStore.dismissLoadingState()
  }

  const searcher = async () => {
    const { calendarPeriodId } = path.params
    loadingStore.setLoadingState()
    const response = await ListCalendarPeriodsGroups(
      String(calendarPeriodId),
      toolset.value?.search ?? ''
    )
    loadingStore.dismissLoadingState()
    calendarPeriodsGroups.data = response.data
    calendarPeriodsGroups.totalItems = response.totalItems
  }

  const pagination = reactive({
    currentPage: 0,
    lastPage: 0,
    limit: 10,
  })

  const toAdd = () => {
    const { id } = path.params
    router.push(
      '/payrollsConfig/' + payrollConfigId.value + '/calendarPeriods/add'
    )
  }

  const currentPageReportTemplate = computed(() => {
    return `{first} a {last} de ${calendarPeriodsGroups.totalItems}`
  })

  const goToDetails = async (id: string) => {
    const { id: payrollConfigId } = path.params
    router.push(
      '/payrollsConfig/' + payrollConfigId + '/calendarPeriods/detail/' + id
    )
  }

  const onDeleteCalendarGroup = async (event: any, id: string) => {
    confirm.require({
      target: event.currentTarget,
      message: '¿Realmente quieres eliminar este registro?',
      icon: 'pi pi-info-circle',
      acceptClass: 'p-button-danger',
      acceptLabel: 'aceptar',
      rejectLabel: 'cancelar',
      acceptIcon: 'pi pi-check',
      accept: async () => {
        loadingStore.setLoadingState()
        const response = await deleteCalendarPeriodsGroup(String(id))
        loadingStore.dismissLoadingState()
        if (response === 'OK') {
          notificationStore.addSuccesNotification({
            detail: `200`,
            severity: 'success',
            summary: `Grupo de periodos eliminado con exito.`,
          })
          await fetchData()
        }
      },
      reject: () => {},
    })
  }

  const tranlateFrequency = (frequency: string) => {
    switch (frequency) {
      case 'weekly':
        return 'Semanal'
      case 'biweekly':
        return 'Bisemanal'
      default:
        return frequency
    }
  }
</script>

<style scoped>
  .tabla {
    margin-top: 1rem;
    box-shadow: 0px 1px 7px rgba(0, 0, 0, 0.1),
      0px 4px 5px -2px rgba(0, 0, 0, 0.12),
      0px 10px 15px -5px rgba(0, 0, 0, 0.2);
    border-radius: 16px;
    width: 90%;
  }

  .actions {
    display: flex;
    width: auto;
    flex-direction: row;
  }

  .action-btn {
    height: 20px;
    width: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  :deep(.p-column-title) {
    font-size: 14px;
  }

  :deep(.p-datatable-tbody) {
    font-size: 14px;
  }

  :deep(.p-paginator) {
    display: flex;
    align-items: center;
    justify-content: end;
    flex-wrap: wrap;
    font-size: 14px;
  }
</style>
-->
